import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useUserAuth } from "../context/userAuthContext";
import { LogIn, Eye, EyeOff, AlertCircle } from "lucide-react";
import GoogleIcon from "../assets/google-icon.png";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { logIn, googleSignIn } = useUserAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      await logIn(email, password);
      navigate("/menu");
    } catch (err) {
      setError("Credenciales incorrectas. Verifica tu email y contraseña.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      await googleSignIn();
      navigate("/menu");
    } catch (error) {
      setError("Error al iniciar sesión con Google. Intenta nuevamente.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12 font-sans">
      <div className="w-full md:max-w-md">
        {/* Login Form */}
        <div className="space-y-6">
          {/* Error Alert */}
          {error && (
            <div className="bg-red-100 border border-[#FF0000] p-4 mb-6 rounded-xl flex items-start">
              <AlertCircle
                className="h-5 w-5 text-[#FF0000] mr-2 mt-0.5 flex-shrink-0"
                strokeWidth={2.5}
              />
              <div>
                <p className="font-bold text-[#FF0000] text-sm uppercase tracking-wide">
                  Error de autenticación
                </p>
                <p className="text-sm text-gray-600">{error}</p>
              </div>
            </div>
          )}

          <h2 className="text-2xl font-bold text-gray-900 text-center">
            Inicia Sesión en Pollo Feliz
          </h2>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Correo Electrónico
              </label>
              <input
                type="email"
                value={email}
                placeholder="correo@pollofeliz.com"
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF0000] focus:border-[#FF0000] outline-none text-gray-900 placeholder-gray-400 bg-white hover:bg-gray-50 transition-all"
                required
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Contraseña
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  placeholder="••••••••••"
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF0000] focus:border-[#FF0000] outline-none text-gray-900 placeholder-gray-400 bg-white hover:bg-gray-50 transition-all"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-[#FF0000] transition-colors"
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" strokeWidth={2} />
                  ) : (
                    <Eye className="h-5 w-5" strokeWidth={2} />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isLoading}
              className={`w-full py-3 px-4 bg-[#FF0000] text-white rounded-xl font-bold text-base flex items-center justify-center gap-2 transition-all ${
                isLoading
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-[#E60000] hover:shadow-md active:scale-95"
              }`}
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Iniciando sesión...
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5" strokeWidth={2.5} />
                  Iniciar Sesión
                </>
              )}
            </button>
          </div>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-3 bg-gray-50 text-gray-500 text-sm font-medium">
                O continúa con
              </span>
            </div>
          </div>

          {/* Google Sign In */}
          <button
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            className={`w-full py-3 px-4 bg-white border border-gray-300 text-gray-900 rounded-xl font-bold text-base flex items-center justify-center gap-2 transition-all ${
              isLoading
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-gray-50 hover:border-[#FF0000] hover:text-[#FF0000] active:scale-95"
            }`}
          >
            <img src={GoogleIcon} alt="Google" className="w-5 h-5 mr-2" />
            Continuar con Google
          </button>

          {/* Sign Up Link */}
          <div className="text-center">
            <p className="text-gray-600 text-sm">
              ¿No tienes una cuenta?{" "}
              <Link
                to="/signup"
                className="font-bold text-[#FF0000] hover:text-[#E60000] transition-colors"
              >
                Regístrate aquí
              </Link>
            </p>
          </div>

          {/* Menu Link */}
          <div className="text-center">
            <Link
              to="/menu"
              className="text-sm text-[#FF0000] hover:text-[#E60000] font-semibold transition-colors"
            >
              Ver menú de comida
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-gray-500 text-sm font-medium">
            © 2025 Pollo Feliz • Todos los derechos reservados
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
